import { SectionTitle } from '@modules/talents/components';
import { SectionCard } from '@modules/talents/components/SectionCard';
import { Box, Chip, Divider, Stack, Typography, styled, useTheme } from '@mui/material';
import { ProductNames, SubscriptionPlanName } from '@hooks/useLimitReached';

const ONE_TIME_PLAN_VALUES = new Set<string>(Object.values(ProductNames));

const isOneTimePlan = (plan: string): boolean => ONE_TIME_PLAN_VALUES.has(plan);

const getPlanDisplayName = (plan: string): string =>
    SubscriptionPlanName[plan] ?? plan;

const Label = styled(Typography)(({ theme }) => ({
    fontFamily: 'Plus Jakarta Sans',
    fontSize: '12px',
    fontWeight: 500,
    color: theme.palette.text.secondary,
    lineHeight: '16px',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
}));

const Value = styled(Typography)(({ theme }) => ({
    fontFamily: 'Plus Jakarta Sans',
    fontSize: '13px',
    fontWeight: 600,
    color: theme.palette.text.primary,
    lineHeight: '16px',
}));

interface JobOfferPlanInfoProps {
    currentPlan?: string | null;
    expiredDate?: string | null;
}

const JobOfferPlanInfo = ({ currentPlan, expiredDate }: JobOfferPlanInfoProps) => {
    const theme = useTheme();

    if (!currentPlan) return null;

    const isOneTime = isOneTimePlan(currentPlan);
    const planTypeLabel = isOneTime ? 'Achat unique' : 'Abonnement';
    const planDisplayName = getPlanDisplayName(currentPlan);
    const formattedExpiry = expiredDate
        ? new Date(expiredDate).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
        : '–';

    const typeColor = isOneTime ? theme.palette.warningOrange.main : theme.palette.successGreen.main;

    return (
        <SectionCard>
            <Stack gap="12px">
                <SectionTitle title="Credits utilisés" />
                <Stack divider={<Divider flexItem />} gap="10px">
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Label>Type</Label>
                        <Chip
                            label={planTypeLabel}
                            size="small"
                            sx={{
                                height: '22px',
                                borderRadius: '6px',
                                backgroundColor: `${typeColor}18`,
                                border: `1px solid ${typeColor}40`,
                                color: typeColor,
                                fontFamily: 'Plus Jakarta Sans',
                                fontSize: '11px',
                                fontWeight: 700,
                                letterSpacing: '0.2px',
                            }}
                        />
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Label>Offre</Label>
                        <Value>{planDisplayName}</Value>
                    </Stack>
                    {expiredDate && <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Label>Expiration</Label>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            {expiredDate && (
                                <Box
                                    sx={{
                                        width: '6px',
                                        height: '6px',
                                        borderRadius: '50%',
                                        backgroundColor: theme.palette.warningOrange.main,
                                        flexShrink: 0,
                                    }}
                                />
                            )}
                            <Typography
                                sx={{
                                    fontFamily: 'Plus Jakarta Sans',
                                    fontSize: '13px',
                                    fontWeight: expiredDate ? 600 : 400,
                                    lineHeight: '16px',
                                    color: expiredDate ? theme.palette.text.primary : theme.palette.text.disabled,
                                    fontStyle: expiredDate ? 'normal' : 'italic',
                                }}
                            >
                                {formattedExpiry}
                            </Typography>
                        </Box>
                    </Stack>}
                </Stack>
            </Stack>
        </SectionCard>
    );
};

export default JobOfferPlanInfo;
